"use client";

import React, { useState } from "react";
import {
  buildOrderItems,
  calculateAmountToPay,
  calculateShippingCost,
  calculateTotalAmountWithOutGST,
  calculateTotalGSTAfterDiscount,
  isOnlyGarrariBikesAvailable,
  steps,
} from "../../utils/utils";
import { Button, Tooltip, useMediaQuery } from "@mui/material";
import EastIcon from "@mui/icons-material/East";
import { useTheme } from "@mui/material/styles";
import toast from "react-hot-toast";
import { FormattedNumber } from "../../utils/numberAnimation";
import { connect } from "react-redux";
import { setIsDoorToDoorChecked } from "../../store/slices/priceRangeSlice";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { EVENTS } from "../../analytics/events";
import { trackEvent } from "../../analytics/gtag";
import CashfreePaymentModal from "../payment/CashfreePaymentModal";

function PriceDetail({
  cart,
  currentStep,
  setCurrentStep,
  useWalletAmount,
  selectedCoupon,
  setUseWalletAmount,
  selectedAddress,
  isDoorToDoorChecked,
  freeShipping,
  userDetail,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const nextStepIndex =
    currentStep < steps.length ? currentStep + 1 : currentStep;
  const nextLabel = steps[nextStepIndex - 1]?.label;

  const [openPaymentModal, setOpenPaymentModal] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState(0);

  const handleProceed = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });

    setCurrentStep((prev, userDetail) => {
      let nextStep = prev;

      if (prev < 2) {
        if (cart && cart.length > 0) {
          trackEvent({
            action: EVENTS.ADDRESS_STEP,
            category: "Address Step",
            label: "User Enter Address Step",
            value: userDetail?.PhoneNumber,
          });
          nextStep = prev + 1;
        } else {
          toast.error("Please add items in cart");
        }
      } else if (prev === 2 && selectedAddress !== null) {
        trackEvent({
          action: EVENTS.COUPON_STEP,
          category: "Coupon Step",
          label: "User Enter Coupon Step",
          value: userDetail?.PhoneNumber,
        });
        nextStep = 3;
      } else if (prev === 2 && selectedAddress === null) {
        toast.error("Please add an address");
      } else if (prev === 3) {
        const totalAmount = calculateAmountToPay(
          cart,
          selectedCoupon?.couponDiscountTaken,
          useWalletAmount,
          setUseWalletAmount,
          isDoorToDoorChecked,
          freeShipping,
          selectedCoupon
        );
        setPaymentAmount(totalAmount);
        setOpenPaymentModal(true);
      }

      return nextStep;
    });
  };

  const { dealerItems, mainItems } = buildOrderItems(
    cart,
    useWalletAmount,
    selectedCoupon,
    isDoorToDoorChecked,
    freeShipping
  );

  // merge all items (dealer + main)
  const allItems = [...dealerItems, ...mainItems];

  return (
    <div className="w-full flex flex-col max-sm:bg-white-1">
      <h3 className="text-[16px] text-black-1 font-bold tracking-wide p-4 border-b-[1px] border-black-1/10">
        Payment Summary
      </h3>
      <div className="w-full flex-col items-center p-4">
        {/* Total Price */}
        <div className="w-full flex items-center justify-between relative mb-4 sm:mb-3">
          <p className="text-[14px] text-black-1/80 font-semibold">
            Total Price
          </p>
          <span className="text-[14px] text-black-1 font-semibold">
            <FormattedNumber
              value={parseInt(calculateTotalAmountWithOutGST(cart))}
            />
          </span>
        </div>

        {/* Shipping */}
        {!isOnlyGarrariBikesAvailable(cart) && (
          <div className="w-full flex flex-col mb-4 sm:mb-3">
            <div className="flex items-center justify-between">
              <p className="text-[14px] text-black-1/80 font-semibold flex items-center">
                Shipping Cost{" "}
                <Tooltip
                  title="Shipping applies for parts and accessories only."
                  placement="right"
                  arrow
                >
                  <InfoOutlinedIcon
                    fontSize="15px"
                    className="text-gray-500 ml-1 cursor-pointer"
                  />
                </Tooltip>
              </p>
              <span className="text-[14px] text-black-1 font-semibold">
                +
                <FormattedNumber
                  value={freeShipping ? 0 : calculateShippingCost(cart)}
                />
              </span>
            </div>
          </div>
        )}

        {/* Coupon */}
        {parseInt(selectedCoupon?.couponDiscountTaken) > 0 && (
          <div className="w-full flex items-center justify-between mb-4 sm:mb-3">
            <p className="text-[14px] text-black-1/80 font-semibold">
              Coupon Discount
            </p>
            <span className="text-[14px] text-black-1 font-semibold">
              -
              <FormattedNumber
                value={parseInt(selectedCoupon?.couponDiscountTaken)}
              />
            </span>
          </div>
        )}

        {/* GST */}
        <div className="w-full flex items-center justify-between mb-4 sm:mb-3">
          <p className="text-[14px] text-black-1/80 font-semibold">
            GST Charges
          </p>
          <span className="text-[14px] text-black-1 font-semibold">
            +
            <FormattedNumber
              value={calculateTotalGSTAfterDiscount(cart, selectedCoupon)}
            />
          </span>
        </div>

        {/* Wallet */}
        {parseInt(useWalletAmount) > 0 && (
          <div className="w-full flex items-center justify-between mb-4 sm:mb-3">
            <p className="text-[14px] text-black-1/80 font-semibold">Wallet</p>
            <span className="text-[14px] text-black-1 font-semibold">
              -<FormattedNumber value={parseInt(useWalletAmount)} />
            </span>
          </div>
        )}

        {/* Amount to Pay */}
        <div className="w-full flex flex-col items-center max-sm:pb-[70px]">
          <div className="w-full flex items-center justify-between pt-4 border-t-[1px] border-black-1/10">
            <p className="text-[16px] text-peach font-semibold">
              Amount to pay
            </p>
            <span className="text-[16px] text-peach font-semibold">
              <FormattedNumber
                value={parseInt(
                  calculateAmountToPay(
                    cart,
                    selectedCoupon?.couponDiscountTaken,
                    useWalletAmount,
                    setUseWalletAmount,
                    isDoorToDoorChecked,
                    freeShipping,
                    selectedCoupon
                  )
                )}
              />
            </span>
          </div>
        </div>

        {/* Proceed Button */}
        <div className="w-full flex flex-col items-center justify-between pt-4">
          <Button
            variant="contained"
            sx={{
              width: "100%",
              height: isMobile ? "40px" : "35px",
              borderRadius: isMobile ? "4px" : "30px",
              background: "#283248",
              fontSize: isMobile ? "16px" : "14px",
              fontWeight: "500",
              color: "white",
              textTransform: "none",
              letterSpacing: "0.8px",
              "&:hover": {
                background: "#283248",
              },
            }}
            onClick={handleProceed}
          >
            Proceed to {nextLabel}
            <EastIcon className="ml-2" sx={{ fontSize: "16px" }} />
          </Button>
          {currentStep <= 2 && (
            <p className="w-full text-[11px] text-start text-gray-500 mt-2">
              <b className="text-[12px]">Note:</b> You can apply a coupon in{" "}
              <strong>Step Payment</strong> during checkout.
            </p>
          )}
        </div>
      </div>

      {/* 💳 Cashfree Payment Modal */}
      <CashfreePaymentModal
        open={openPaymentModal}
        onClose={() => setOpenPaymentModal(false)}
        userID={userDetail && userDetail?.UserID && userDetail?.UserID}
        amount={parseInt(paymentAmount)}
        shippingID={selectedAddress && selectedAddress}
        coupon={selectedCoupon}
        walletAmount={useWalletAmount}
        shippingAmount={calculateShippingCost(cart)}
        credit={null}
        items={allItems}
        cart={cart}
        selectedCoupon={selectedCoupon}
        useWalletAmount={useWalletAmount}
        setUseWalletAmount={setUseWalletAmount}
        isDoorToDoorChecked={isDoorToDoorChecked}
        freeShipping={freeShipping}
        userDetail={userDetail}
      />
    </div>
  );
}

const mapStateToProps = (state) => ({
  isDoorToDoorChecked: state.priceRange.isDoorToDoorChecked,
});

const mapDispatchToProps = {
  setIsDoorToDoorChecked,
};

export default connect(mapStateToProps, mapDispatchToProps)(PriceDetail);
